import { Accordion, AccordionTab } from "primereact/accordion";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import lessonServices from "../../../services/lessonServices";
import ImageViewer from "../../ImageViewer";
import LoadingSpinner from "../../LoadingSpinner";
import Modal from "../../Modal";

const QuestionsSolutionsModal = forwardRef(({}, ref) => {
  const { t } = useTranslation();

  const [selectedLessonID, setSelectedLessonID] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lessonIDs, setLessonIDs] = useState([]);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const [lessonName, setLessonName] = useState("");
  const [lessonsData, setLessonsData] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(null); // Track active tab index

  useImperativeHandle(ref, () => ({
    openModal(lessonID, lessonsData, index, courseDetails) {
      setSelectedLessonID(lessonID);
      setLessonsData(lessonsData);
      setCourseDetails(courseDetails);

      setCurrentLessonIndex(index);
      const lessonIDsList = lessonsData.map((item) => item?.lesson?.id);

      setLessonIDs(lessonIDsList);

      const currentLesson = lessonsData.find((item) => item?.lesson?.id === lessonID);
      setLessonName(currentLesson?.lesson?.title || "");

      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
    setActiveTabIndex(null); // Clear questions when modal is closed
  };

  const fetchLessonQuestions = async (lessonID) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await lessonServices.getLessonQuestions(lessonID);
      setQuestions(response || []);
    } catch (error) {
      setError(t("failed_to_load_questions"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchLessonQuestions(selectedLessonID);
    }

    return () => {
      setActiveTabIndex(null); // Clear questions when modal is closed
    };
  }, [isOpen, selectedLessonID]);

  useEffect(() => {
    const currentLesson = lessonsData?.find((item) => item?.lesson?.id === selectedLessonID);
    setLessonName(currentLesson?.lesson?.title || "");
  }, [selectedLessonID, currentLessonIndex]);

  const goToNextLesson = () => {
    if (currentLessonIndex < lessonIDs.length - 1) {
      setCurrentLessonIndex(currentLessonIndex + 1);
      setSelectedLessonID(lessonIDs[currentLessonIndex + 1]);
    }
  };

  const goToPreviousLesson = () => {
    if (currentLessonIndex > 0) {
      setCurrentLessonIndex(currentLessonIndex - 1);
      setSelectedLessonID(lessonIDs[currentLessonIndex - 1]);
    }
  };

  const renderOptions = (question) => {
    switch (question?.type?.id) {
      case 1:
        return question.options.map((option, idx) => (
          <div key={idx} className="flex space-x-2 rounded-lg p-2">
            <span className={`font-semibold ${option.is_correct ? "text-green-600" : "text-red-600"}`}>
              {option.is_correct ? "✔" : "✖"}
            </span>

            {courseDetails?.ide == "Web-development" ? (
              <p className="text-start">{option.text}</p>
            ) : (
              <p className={`text-start`} dangerouslySetInnerHTML={{ __html: option.text }} />
            )}
          </div>
        ));
      case 2:
        return question.options.map((option, idx) => (
          <div key={idx}>
            <img
              src={option?.image}
              className={`flex h-36 w-36 space-x-2 rounded-xl object-contain p-2 ${
                option.is_correct ? "border-2 border-dotted border-green-600" : ""
              }`}
              alt=""
            />
          </div>
        ));
      default:
        return null;
    }
  };

  const handleTabChange = (index) => {
    setActiveTabIndex((prevIndex) => (prevIndex === index ? null : index)); // Close if clicked again
  };

  return (
    <Modal
      isFullScreen
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <p className="text-2xl text-blue-500">
          {t("questions_solutions")} - {lessonName}
        </p>
      }
      // footer={
      //   <div className="flex justify-between">
      //     <Button
      //       iconLeft={<MdChevronLeft />}
      //       variant="text"
      //       onClick={goToPreviousLesson}
      //       disabled={currentLessonIndex === 0}
      //       label={t("previous")}
      //     />
      //     <Button
      //       iconRight={<MdChevronRight />}
      //       variant="text"
      //       onClick={goToNextLesson}
      //       disabled={currentLessonIndex === lessonIDs.length - 1}
      //       label={t("next")}
      //     />
      //   </div>
      // }
    >
      <div className="p-5 text-center text-gray-700">
        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : questions?.length === 0 ? (
          <p>{t("no_questions_available")}</p>
        ) : (
          questions.length > 0 && (
            <Accordion>
              {questions.map((questionItem, index) => (
                <AccordionTab
                  key={index}
                  pt={{
                    headeraction:
                      activeTabIndex === index ? "bg-blue-500 text-white" : "bg-[#2B2D4B] text-white",
                    content: "p-0 pt-5",
                  }}
                  header={<div>{questionItem?.skill?.name}</div>}
                  onClick={() => handleTabChange(index)} // Handle tab click to change active tab index
                >
                  {questionItem.questions.map((question, idx) => (
                    <div
                      key={idx}
                      className={`mb-6 rounded-md border bg-background p-4 shadow-lg dark:bg-dark-background`}
                    >
                      <p
                        className="text-start font-semibold text-gray-800"
                        dangerouslySetInnerHTML={{ __html: question?.text }}
                      />
                      {question?.image && <ImageViewer src={question.image} className="mb-4" />}
                      <div
                        className={`${question?.type?.id === 2 ? "mt-3 grid grid-cols-2 gap-2" : "mt-3 flex flex-col gap-2"} `}
                      >
                        {renderOptions(question)}
                      </div>
                    </div>
                  ))}
                </AccordionTab>
              ))}
            </Accordion>
          )
        )}
      </div>
    </Modal>
  );
});

export default QuestionsSolutionsModal;
